import React from 'react';
import { ModerationModel } from './useModeration';
import { SEVERITY_CONFIG, TYPE_LABELS, CATEGORY_LABELS } from './moderationTypes';

const sevConfig = (s: string) => SEVERITY_CONFIG[s] || SEVERITY_CONFIG.low;

const ModerationQueue: React.FC<{ model: ModerationModel }> = ({ model }) => {
  const {
    stats, reports, loading, filterStatus, setFilterStatus, filterType, setFilterType,
    filterSeverity, setFilterSeverity, fetchReports, fetchStats, loadRiskProfile,
    timeAgo, formatDate, inputStyle, openReview,
  } = model;

  return (
    <>
      {/* Stats Bar */}
      {stats && (
        <div className="rp-bento-card mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {[
              { label: 'Pending', value: stats.pending_reports, color: stats.pending_reports > 0 ? 'var(--admin-error)' : '#6b7280' },
              { label: 'Under Review', value: stats.under_review, color: 'var(--admin-warning)' },
              { label: 'Actioned (7d)', value: stats.actioned_this_week, color: 'var(--admin-success)' },
              { label: 'Repeat Offenders', value: stats.repeat_offenders, color: stats.repeat_offenders > 0 ? 'var(--admin-error)' : '#6b7280' },
              { label: 'Suspended Users', value: stats.users_currently_suspended, color: stats.users_currently_suspended > 0 ? 'var(--admin-warning)' : '#6b7280' },
              { label: 'Chat Bans', value: stats.chat_bans_active, color: 'var(--rp-primary)' },
            ].map(s => (
              <div key={s.label} className="text-center p-3 rounded-lg" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--rp-text-secondary)' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Severity breakdown for pending */}
          {stats.by_severity && Object.keys(stats.by_severity).length > 0 && (
            <div className="flex gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--rp-border)' }}>
              <span className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>Pending by severity:</span>
              {Object.entries(stats.by_severity).map(([sev, cnt]) => (
                <span key={sev} className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: sevConfig(sev).bg, color: sevConfig(sev).color }}>
                  {cnt} {sevConfig(sev).label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="rp-bento-card mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label htmlFor="mod-filter-status" className="text-xs block mb-1" style={{ color: 'var(--rp-text-tertiary)' }}>Status</label>
            <select id="mod-filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm" style={inputStyle}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="dismissed">Dismissed</option>
              <option value="warning_issued">Warning Issued</option>
              <option value="actioned">Actioned</option>
            </select>
          </div>
          <div>
            <label htmlFor="mod-filter-type" className="text-xs block mb-1" style={{ color: 'var(--rp-text-tertiary)' }}>Type</label>
            <select id="mod-filter-type" value={filterType} onChange={e => setFilterType(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm" style={inputStyle}>
              <option value="">All Types</option>
              {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="mod-filter-severity" className="text-xs block mb-1" style={{ color: 'var(--rp-text-tertiary)' }}>Severity</label>
            <select id="mod-filter-severity" value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm" style={inputStyle}>
              <option value="">All</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div className="ml-auto">
            <button onClick={() => { fetchReports(); fetchStats(); }} className="admin-btn-secondary px-4 py-2 rounded-lg text-sm">
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Reports Queue */}
      <div className="rp-bento-card h-full">
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--rp-text-secondary)' }}>
          {reports.length} report{reports.length !== 1 ? 's' : ''}
        </h3>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-6 h-6 border-2 rounded-full mx-auto mb-3" style={{ borderColor: 'var(--rp-border)', borderTopColor: 'var(--rp-text-secondary)' }} />
            <p className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>Loading reports...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm" style={{ color: 'var(--rp-text-tertiary)' }}>
              {filterStatus === 'pending' ? 'No pending reports — all clear!' : 'No reports match filters'}
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {reports.map(report => {
              const sev = sevConfig(report.severity);
              return (
                <div key={report.id} className="p-3 rounded-lg transition-all"
                  style={{ backgroundColor: 'var(--rp-surface-secondary)', border: '1px solid var(--rp-border)' }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Header row: severity + type + category + time */}
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: sev.bg, color: sev.color }}>
                          {sev.label}
                        </span>
                        <span className="text-xs font-semibold" style={{ color: 'var(--rp-text)' }}>
                          {TYPE_LABELS[report.report_type] || report.report_type}
                        </span>
                        <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--rp-surface)', color: 'var(--rp-text-secondary)' }}>
                          {CATEGORY_LABELS[report.category] || report.category}
                        </span>
                        <span className="text-[10px]" style={{ color: 'var(--rp-text-tertiary)' }}>{timeAgo(report.created_at)}</span>
                      </div>

                      {/* Reported user */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>Against:</span>
                        <button onClick={() => loadRiskProfile(report.reported_user_id)}
                          className="text-sm font-semibold hover:underline" style={{ color: 'var(--rp-text)' }}>
                          {report.reported_display_name || report.reported_username || report.reported_email || report.reported_user_id.slice(0, 8)}
                        </button>
                        {report.total_reports_against_user > 1 && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/20 text-red-400">
                            {report.total_reports_against_user} reports
                          </span>
                        )}
                      </div>

                      {/* Reason */}
                      <p className="text-xs line-clamp-2" style={{ color: 'var(--rp-text-secondary)' }}>{report.reason}</p>

                      {/* Reporter + event context */}
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-[10px]" style={{ color: 'var(--rp-text-tertiary)' }}>
                          By: {report.reporter_display_name || report.reporter_username || 'Unknown'}
                        </span>
                        {report.event_title && (
                          <span className="text-[10px]" style={{ color: 'var(--rp-text-tertiary)' }}>
                            Event: {report.event_title}
                          </span>
                        )}
                      </div>

                      {/* Review info if already reviewed */}
                      {report.reviewed_by && (
                        <div className="mt-1 text-[10px]" style={{ color: 'var(--rp-text-tertiary)' }}>
                          Reviewed by {report.reviewed_by} · {formatDate(report.reviewed_at!)}
                          {report.action_taken && <> · Action: {report.action_taken}</>}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {(report.status === 'pending' || report.status === 'under_review') && (
                      <div className="flex-shrink-0">
                        <button onClick={() => openReview(report)}
                          className="admin-btn-secondary px-3 py-1.5 rounded-lg text-xs font-semibold"
                        >
                          Review
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default ModerationQueue;
